import * as Joi from "joi";
import { Moment } from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import CmPermission from '../cm-permissions/cm-permission.entity';
import { DEPARTMENTS } from '../enums/enums';
import Episodic from '../episodics/episodic.entity';
import HaPermission from '../ha-permissions/ha-permission.entity';
import MuPermission from '../mu-permissions/mu-permission.entity';
import PrPermission from '../pr-permissions/pr-permission.entity';
import ProdPermission from '../prod-permissions/prod-permission.entity';
import SetPermission from '../set-permissions/set-permission.entity';
import UserBase from '../user-base.entity';
import User from '../users/user.entity';

@Entity('permissions')
export default class Permission extends UserBase {
	static tableName = 'permissions';

	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	user_id: number;

	@Column('int')
	episodic_id: number;

	@Column('int')
	actor_contact: number;

	@Column('int')
	admin_access: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('varchar')
	email: string;

	@Column('int')
	current_department: DEPARTMENTS;

	@Column({ type: 'int', transformer: booleanTransformer })
	watermark: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	mobile_only: boolean;

	@Column({ type: 'int', transformer: booleanTransformer })
	archive_user: boolean;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@OneToMany(
		(type) => ProdPermission,
		(prodPermission) => prodPermission.permission
	)
	prodPermissions: ProdPermission[];

	@OneToOne((type) => CmPermission, (cmPermission) => cmPermission.permission)
	cmPermission: CmPermission;

	@OneToOne((type) => HaPermission, (haPermission) => haPermission.permission)
	haPermission: HaPermission;

	@OneToOne((type) => MuPermission, (muPermission) => muPermission.permission)
	muPermission: MuPermission;

	@OneToOne((type) => PrPermission, (prPermission) => prPermission.permission)
	prPermission: PrPermission;

	@OneToOne(
		(type) => SetPermission,
		(setPermission) => setPermission.permission
	)
	setPermission: SetPermission;

	@ManyToOne((type) => User, (user) => user.permissions)
	@JoinColumn({
		name: 'user_id',
	})
	user: User;

	@ManyToOne((type) => Episodic, (episodic) => episodic.permissions)
	@JoinColumn({ name: 'episodic_id' })
	episodic: Episodic;

	static rules() {
		return {
			id: Joi.number().integer(),
			user_id: Joi.number().integer().allow(null),
			episodic_id: Joi.number().integer().allow(null),
			actor_contact: Joi.number().integer(),
			admin_access: Joi.number().integer(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			email: Joi.string().max(255).allow(null),
			current_department: Joi.number().integer(),
			watermark: Joi.boolean(),
			mobile_only: Joi.boolean(),
			archive_user: Joi.boolean().allow(null),
			created_by: Joi.number().integer().allow(null),
			date_created: Joi.date().allow(null),
		};
	}

	baseToJSON() {
		return {
			id: this.id,
			user_id: this.user_id,
			episodic_id: this.episodic_id,
			actor_contact: this.actor_contact,
			admin_access: this.admin_access,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			email: this.email,
			current_department: this.current_department,
			watermark: this.watermark,
			mobile_only: this.mobile_only,
			archive_user: this.archive_user,
			created_by: this.created_by,
			date_created: this.date_created,
		};
	}

	toJSON() {
		return {
			...this.baseToJSON(),
			prodPermissions: this.prodPermissions ? this.prodPermissions : [],
			cmPermission: this.cmPermission,
			haPermission: this.haPermission,
			muPermission: this.muPermission,
			setPermission: this.setPermission,
			prPermission: this.prPermission,
		};
	}

	assign(props?: GenPartial<Permission>) {
		super.assign(props);

		if (props) {
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
			if (typeof props.watermark !== 'undefined') {
				this.watermark = booleanTransformer.from(props.watermark);
			}
			if (typeof props.mobile_only !== 'undefined') {
				this.mobile_only = booleanTransformer.from(props.mobile_only);
			}
			if (typeof props.archive_user !== 'undefined') {
				this.archive_user = booleanTransformer.from(props.archive_user);
			}
			if (typeof props.prodPermissions !== 'undefined') {
				this.prodPermissions = props.prodPermissions.map(
					(prodPerm) => new ProdPermission(prodPerm)
				);
			}
			if (
				typeof props.cmPermission !== 'undefined' &&
				props.cmPermission !== null
			) {
				this.cmPermission = new CmPermission(props.cmPermission);
			}
			if (
				typeof props.haPermission !== 'undefined' &&
				props.haPermission !== null
			) {
				this.haPermission = new HaPermission(props.haPermission);
			}
			if (
				typeof props.muPermission !== 'undefined' &&
				props.muPermission !== null
			) {
				this.muPermission = new MuPermission(props.muPermission);
			}
			if (
				typeof props.prPermission !== 'undefined' &&
				props.prPermission !== null
			) {
				this.prPermission = new PrPermission(props.prPermission);
			}
			if (
				typeof props.setPermission !== 'undefined' &&
				props.setPermission !== null
			) {
				this.setPermission = new SetPermission(props.setPermission);
			}
		}
	}

	constructor(props?: GenPartial<Permission>) {
		super(props);
	}

	canEditScenesAndCharacters(): boolean {
		return !!this.admin_access;
	}
}
